import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '~/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/utils/cn';

interface FullPaginationProps {
  /** 1-based current page number. */
  page: number;
  /** Number of rows per page. */
  pageSize: number;
  /** Total number of rows across all pages. */
  totalRows: number;
  /** Available page size options. Omit to hide the rows-per-page selector. */
  pageSizeOptions?: number[];
  /** Called when the user navigates to a different page. */
  onPageChange: (page: number) => void;
  /** Called when the user selects a different page size. Required when pageSizeOptions is provided. */
  onPageSizeChange?: (size: number) => void;
  className?: string;
  /** `data-testid` root; derives `-summary`, `-page-size-select`, `-prev-button`, `-next-button`, `-page-${n}`. */
  testId?: string;
}

function getPageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  const pages: (number | null)[] = [1];
  if (left > 2) pages.push(null);
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < total - 1) pages.push(null);
  pages.push(total);
  return pages;
}

/**
 * Standalone full pagination bar with optional rows-per-page selector and
 * "Showing X–Y of Z" label. Reusable outside DataTable.
 */
export function FullPagination({
  page,
  pageSize,
  totalRows,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  className,
  testId = 'full-pagination',
}: FullPaginationProps) {
  const totalPages = Math.ceil(totalRows / pageSize);

  const pageNumbers = getPageNumbers(page, totalPages);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  return (
    <div className={cn('flex items-center justify-between gap-4', className)} data-testid={testId}>
      <p
        role="status"
        aria-live="polite"
        className="text-muted-foreground shrink-0 text-sm"
        data-testid={`${testId}-summary`}
      >
        Showing {String(start)}–{String(end)} of {String(totalRows)}
      </p>

      <div className="flex items-center gap-3">
        {pageSizeOptions !== undefined && pageSizeOptions.length > 0 && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-sm">Items per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                onPageSizeChange(Number(v));
              }}
            >
              <SelectTrigger className="h-8 w-17.5" data-testid={`${testId}-page-size-select`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid={`${testId}-page-size-select-content`}>
                {pageSizeOptions.map((size) => (
                  <SelectItem
                    key={size}
                    value={String(size)}
                    data-testid={`${testId}-page-size-option-${String(size)}`}
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination className="w-auto">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  text="Prev"
                  data-testid={`${testId}-prev-button`}
                  aria-disabled={page <= 1}
                  className={cn('cursor-pointer', page <= 1 && 'cursor-not-allowed opacity-50')}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) onPageChange(page - 1);
                  }}
                />
              </PaginationItem>

              {pageNumbers.map((p, i) =>
                p === null ? (
                  <PaginationItem key={`ellipsis-${String(i)}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      data-testid={`${testId}-page-${String(p)}`}
                      aria-label={`Page ${String(p)}`}
                      className={cn(
                        'cursor-pointer',
                        p === page &&
                          'bg-primary hover:bg-primary/90 border-primary text-white hover:text-white',
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}

              <PaginationItem>
                <PaginationNext
                  data-testid={`${testId}-next-button`}
                  aria-disabled={page >= totalPages}
                  className={cn(
                    'cursor-pointer',
                    page >= totalPages && 'cursor-not-allowed opacity-50',
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page < totalPages) onPageChange(page + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
